import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { SideDrawer } from '@sales-center/components'
import { AddServiceForm } from './AddServiceForm'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { VerboseContent } from './VerboseContent'
import { useData } from './hooks'
import { SaveServicesForLaterDialog } from './SaveServicesForLaterDialog/SaveServicesForLaterDialog'
import { AddServiceProductButton } from './AddServiceProductButton'
import { SxProps } from '@mui/material/styles'
import { toast } from 'react-toastify'
import useMediaQuery from '@mui/material/useMediaQuery'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'

export interface Props {
  address: string
  branchId: string
  buttonFullWidth?: boolean
  buttonClassName?: SxProps
  buttonVariant?: 'contained' | 'outlined'
  deleteServiceWarningMessage?: string
  entityId: number
  entityType: ENTITY_TYPES
  locationId?: string
  minimumServicesCount?: number
  openAddServiceDrawerOnMount?: boolean
  readOnly?: boolean
  useSystemName?: boolean
  serviceLocations: LocationDTO[]
  serviceLocationsLoading: boolean
  totalServicesCount?: number
  variant?: 'basic' | 'verbose'
  createNewOpp?: boolean
  newScreen?: boolean
}

export function Services(props: Props) {
  const { serviceLocations, variant = 'basic' } = props
  const isMobileViewport = useMediaQuery('(max-width:900px)')

  const {
    disabled,
    editService,
    loading,
    localMode,
    onClose,
    onCloseSaveForLater,
    onEdit,
    onOpen,
    onOpenSaveForLater,
    open,
    saveForLaterOpen,
    serviceFrequencies,
    serviceProducts,
    tabletView,
    isClosedEntity,
    totalServicesCount,
    nextSortOrderMap,
    saveForLaterToastIdRef,
  } = useData({ ...props })

  const [isMobile, setIsMobile] = useState(isMobileViewport)
  useEffect(() => {
    setIsMobile(isMobileViewport)
  }, [isMobileViewport])

  useEffect(() => {
    return () => {
      if (saveForLaterToastIdRef.current) {
        toast.dismiss(saveForLaterToastIdRef.current)
      }
    }
  }, [saveForLaterToastIdRef])

  const serviceFormContent = useMemo(
    () => (
      <AddServiceForm
        {...props}
        service={editService}
        onClose={onClose}
        serviceProducts={serviceProducts}
        serviceFrequencies={serviceFrequencies}
        serviceLocations={serviceLocations}
        localMode={localMode}
        nextSortOrder={nextSortOrderMap}
        isMobile={isMobile}
      />
    ),
    [
      editService,
      isMobile,
      localMode,
      nextSortOrderMap,
      onClose,
      props,
      serviceFrequencies,
      serviceLocations,
      serviceProducts,
    ],
  )

  return (
    <>
      {isMobile ? (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth={'sm'}
          scroll="paper"
          PaperProps={{
            sx: {
              minWidth: {
                xs: '90vw',
                sm: '501px',
              },
              maxHeight: '85vh',
            },
          }}
        >
          <DialogTitle>
            {editService === undefined
              ? 'Add Service or Product'
              : 'Update Service or Product'}
          </DialogTitle>
          {serviceFormContent}
        </Dialog>
      ) : (
        <SideDrawer
          title={
            editService === undefined
              ? 'Add Service or Product'
              : 'Update Service or Product'
          }
          open={open}
          onClose={onClose}
          width={520}
        >
          {serviceFormContent}
        </SideDrawer>
      )}

      <SaveServicesForLaterDialog
        {...props}
        disabled={disabled}
        loading={loading}
        localMode={localMode}
        serviceFrequencies={serviceFrequencies}
        open={saveForLaterOpen}
        onClose={onCloseSaveForLater}
        totalServicesCount={totalServicesCount || 0}
        saveForLaterToastIdRef={saveForLaterToastIdRef}
      />

      {variant === 'basic' ? (
        <AddServiceProductButton
          {...props}
          disabled={disabled}
          loading={loading}
          addServiceButtonClick={onOpen}
        />
      ) : (
        serviceLocations.length > 0 && (
          <VerboseContent
            {...props}
            disabled={disabled}
            onAddClick={onOpen}
            onEditClick={onEdit}
            loading={loading}
            localMode={localMode}
            onSaveServicesForLaterClick={onOpenSaveForLater}
            serviceFrequencies={serviceFrequencies}
            tabletView={tabletView}
            isClosedEntity={isClosedEntity}
            totalServicesCount={totalServicesCount}
          />
        )
      )}
    </>
  )
}
